import { useMemo } from 'react';

/** One mappable target field (e.g. Date, Amount, Donor name) the grid offers. */
export interface ColumnTarget {
  /** Stable key written into the assignments record (e.g. 'date', 'amount'). */
  value: string;
  /** Treasurer-first label rendered in the dropdown. */
  label: string;
}

export interface ColumnMappingGridProps {
  /** The source file's column headers, in order. */
  headers: string[];
  /** The mappable target fields for this import kind. */
  targets: ColumnTarget[];
  /**
   * The current mapping: target key → the column index it's assigned to. Starts
   * as the deterministic fuzzy guesses and then carries the user's picks back
   * down, because the grid is controlled — the caller owns the mapping. A future
   * LLM pre-fill would seed this SAME shape — the named, deferred seam (no LLM
   * in v1).
   */
  assignments: Record<string, number | null>;
  /**
   * Fires whenever the mapping changes, with the resolved target → column-index
   * record (first column wins per target; unmapped columns omitted).
   */
  onChange: (assignments: Record<string, number | null>) => void;
}

const SKIP_VALUE = '';

/**
 * The universal per-column mapping grid. Generalized from the bank
 * <code>BankColumnMappingPanel</code>'s per-header dropdowns so the bank flow
 * AND the off-platform Donations/Expenses flow share one component (no
 * duplicated grid). The caller supplies the target field set per import kind;
 * the grid owns only the column → target selection UI and the first-wins
 * target→index reduction.
 *
 * The grid owns its per-column selection state (seeded from
 * <code>initialAssignments</code>, re-seeded when that pre-fill changes — e.g. a
 * fresh preview). It reports the first-wins target→index reduction via
 * <code>onChange</code>; the parent decides what to do with the assignments
 * (build a bank mapping, a compiled mapping, re-preview, etc.).
 */
export function ColumnMappingGrid({
  headers,
  targets,
  assignments,
  onChange,
}: ColumnMappingGridProps) {
  // Per-column selected target value, derived from the caller's assignments. A
  // column shows the target whose index === the column; everything else skips.
  const perColumn = useMemo(
    () => seedPerColumn(headers, targets, assignments),
    [headers, targets, assignments],
  );

  const handleColumnChange = (idx: number, nextTarget: string) => {
    const next = [...perColumn];
    next[idx] = nextTarget;
    onChange(reduceAssignments(next, targets));
  };

  return (
    <section>
      <h4 className="mb-2 text-sm font-medium">Map each column:</h4>
      <ul className="space-y-1">
        {headers.map((header, idx) => (
          <li key={idx} className="flex items-center gap-3">
            <span className="w-1/2 truncate rounded bg-muted px-2 py-1 text-xs">
              {header || `Column ${idx + 1}`}
            </span>
            <span aria-hidden className="text-muted-foreground">
              →
            </span>
            <select
              aria-label={`Column ${idx + 1} maps to`}
              value={perColumn[idx] ?? SKIP_VALUE}
              onChange={(e) => handleColumnChange(idx, e.target.value)}
              className="rounded-md border px-2 py-1 text-sm"
            >
              <option value={SKIP_VALUE}>— skip —</option>
              {targets.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Project a target→index mapping onto the per-column selection the grid shows.
 * Column <code>idx</code> shows the target assigned to idx; ties (two targets
 * claiming the same column — shouldn't happen) take the first target.
 */
function seedPerColumn(
  headers: string[],
  targets: ColumnTarget[],
  assignments: Record<string, number | null>,
): string[] {
  return headers.map((_, idx) => {
    const hit = targets.find((t) => assignments[t.value] === idx);
    return hit ? hit.value : SKIP_VALUE;
  });
}

/**
 * Reduce per-column target selections to a target→column-index record. First
 * column wins per target (a later duplicate selection is ignored) — mirrors the
 * bank panel's original first-wins reducer.
 */
function reduceAssignments(
  perColumn: string[],
  targets: ColumnTarget[],
): Record<string, number | null> {
  const valid = new Set(targets.map((t) => t.value));
  const out: Record<string, number | null> = {};
  perColumn.forEach((target, idx) => {
    if (!target || !valid.has(target)) return;
    if (out[target] == null) out[target] = idx;
  });
  return out;
}
